// Formulario del contenido de una sección, generado de su config (ver fields.ts).

import { useState } from 'react';
import { ChevronDown, ChevronRight, ImageOff, Link2, Plus, Trash2 } from 'lucide-react';

import type { Config, Json } from './api';
import { blankLike, humanize, isFormable, itemTitle, kindOf, setAt } from './fields';

type Path = (string | number)[];

const inputCls =
  'w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30';

function fid(path: Path) {
  return `se-f-${path.join('-')}`;
}

function ImageThumb({ src }: { src: string }) {
  const [bad, setBad] = useState(false);
  if (!src) return null;
  return (
    <div className="mt-2 flex h-24 items-center justify-center overflow-hidden rounded-xl border border-border bg-muted">
      {bad ? (
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><ImageOff size={14} aria-hidden /> No se pudo cargar la imagen</span>
      ) : (
        <img src={src} alt="" className="h-full w-full object-cover" onError={() => setBad(true)} onLoad={() => setBad(false)} />
      )}
    </div>
  );
}

function Leaf({ path, k, value, onSet }: { path: Path; k: string; value: Json; onSet: (p: Path, v: Json | undefined) => void }) {
  const kind = kindOf(k, value);
  const id = fid(path);
  const label = humanize(k);
  if (kind === 'bool') {
    return (
      <label htmlFor={id} className="flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-2.5">
        <span className="text-sm text-foreground">{label}</span>
        <input id={id} type="checkbox" checked={value as boolean} onChange={(e) => onSet(path, e.target.checked)} className="h-4 w-4 accent-primary" />
      </label>
    );
  }
  return (
    <div>
      <label htmlFor={id} className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        {kind === 'url' && <Link2 size={12} aria-hidden />}
        {label}
      </label>
      {kind === 'textarea' ? (
        <textarea id={id} rows={3} value={value as string} onChange={(e) => onSet(path, e.target.value)} className={`${inputCls} resize-y`} />
      ) : kind === 'number' ? (
        <input id={id} type="number" value={value as number} onChange={(e) => onSet(path, e.target.value === '' ? 0 : Number(e.target.value))} className={inputCls} />
      ) : (
        <input
          id={id}
          type={kind === 'url' || kind === 'image' ? 'url' : 'text'}
          inputMode={kind === 'url' || kind === 'image' ? 'url' : undefined}
          placeholder={kind === 'image' ? 'https://…/imagen.jpg' : kind === 'url' ? 'https://… · /pagina · #seccion' : undefined}
          value={value as string}
          onChange={(e) => onSet(path, e.target.value)}
          className={inputCls}
        />
      )}
      {kind === 'image' && <ImageThumb src={value as string} />}
    </div>
  );
}

function Node({ path, k, value, depth, onSet }: { path: Path; k: string; value: Json; depth: number; onSet: (p: Path, v: Json | undefined) => void }) {
  const [open, setOpen] = useState<Record<number, boolean>>({ 0: true });
  if (!isFormable(value, depth)) return null;
  if (value === null || typeof value !== 'object') return <Leaf path={path} k={k} value={value} onSet={onSet} />;

  if (Array.isArray(value)) {
    const strings = typeof value[0] === 'string';
    return (
      <fieldset className="rounded-2xl border border-border p-3">
        <legend className="px-1 text-xs font-semibold text-foreground">{humanize(k)} <span className="font-normal text-muted-foreground">· {value.length}</span></legend>
        <ul className="flex flex-col gap-2">
          {value.map((item, i) => (
            <li key={i} className={strings ? 'flex items-center gap-2' : 'rounded-xl border border-border bg-background/60'}>
              {strings ? (
                <>
                  <input aria-label={`${humanize(k)} ${i + 1}`} value={item as string} onChange={(e) => onSet([...path, i], e.target.value)} className={inputCls} />
                  <button type="button" onClick={() => onSet([...path, i], undefined)} aria-label={`Quitar ${humanize(k)} ${i + 1}`} className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-destructive"><Trash2 size={15} /></button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-1 px-2 py-1.5">
                    <button
                      type="button"
                      onClick={() => setOpen((o) => ({ ...o, [i]: !o[i] }))}
                      aria-expanded={!!open[i]}
                      className="flex min-w-0 flex-1 items-center gap-1.5 rounded-lg px-1 py-1 text-left text-sm font-medium text-foreground hover:bg-muted"
                    >
                      {open[i] ? <ChevronDown size={15} aria-hidden /> : <ChevronRight size={15} aria-hidden />}
                      <span className="truncate">{itemTitle(item, i)}</span>
                    </button>
                    <button type="button" onClick={() => onSet([...path, i], undefined)} aria-label={`Quitar «${itemTitle(item, i)}»`} className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-destructive"><Trash2 size={15} /></button>
                  </div>
                  {open[i] && (
                    <div className="flex flex-col gap-3 border-t border-border p-3">
                      {Object.entries(item as Record<string, Json>).map(([ck, cv]) => (
                        <Node key={ck} path={[...path, i, ck]} k={ck} value={cv} depth={depth + 2} onSet={onSet} />
                      ))}
                    </div>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => {
            onSet([...path, value.length], blankLike(value[value.length - 1]));
            setOpen((o) => ({ ...o, [value.length]: true }));
          }}
          className="mt-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-primary hover:bg-primary/10"
        >
          <Plus size={15} aria-hidden /> Agregar
        </button>
      </fieldset>
    );
  }

  return (
    <fieldset className="flex flex-col gap-3 rounded-2xl border border-border p-3">
      <legend className="px-1 text-xs font-semibold text-foreground">{humanize(k)}</legend>
      {Object.entries(value).map(([ck, cv]) => (
        <Node key={ck} path={[...path, ck]} k={ck} value={cv} depth={depth + 1} onSet={onSet} />
      ))}
    </fieldset>
  );
}

export function FieldsForm({ value, onChange }: { value: Config; onChange: (next: Config) => void }) {
  const onSet = (p: Path, v: Json | undefined) => onChange(setAt(value, p, v));
  const entries = Object.entries(value).filter(([, v]) => isFormable(v));
  if (entries.length === 0) {
    return <p className="rounded-xl border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">Esta sección no tiene textos editables por formulario. Usa el editor avanzado de abajo.</p>;
  }
  // Primero lo simple (textos, imágenes), luego grupos y listas.
  const simple = entries.filter(([, v]) => typeof v !== 'object');
  const groups = entries.filter(([, v]) => typeof v === 'object');
  return (
    <div className="flex flex-col gap-3">
      {[...simple, ...groups].map(([k, v]) => (
        <Node key={k} path={[k]} k={k} value={v} depth={0} onSet={onSet} />
      ))}
    </div>
  );
}
